import { DelegationBuyer } from '@1po/1po-bff-fe-spec/generated/delegation/response/model/DelegationBuyer';
import { DelegationSeller } from '@1po/1po-bff-fe-spec/generated/delegation/response/model/DelegationSeller';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { FOUND, LOADING, NOT_FOUND, SEARCH_STATUS } from 'utils';
import {
  CANCEL_CURRENT_DELEGATION_SELLER,
  CONNECT_DELEGATION_SESSION_REQUEST,
  CONNECT_DELEGATION_SESSION_RESPONSE,
  ConnectDelegationRequest,
  DELEGATION_SESSION_ERROR,
  DELEGATION_SESSION_NAMESPACE,
  DELEGATION_SESSION_UPDATED_EVENT,
  DelegationSessionResponse,
  DelegationSessionState,
  DISCONNECT_DELEGATION_SESSION_REQUEST,
  DISCONNECT_DELEGATION_SESSION_RESPONSE,
  EXTEND_DELEGATION_SESSION_REQUEST,
  EXTEND_DELEGATION_SESSION_RESPONSE,
  GET_ACTIVE_DELEGATION_SESSION_REQUEST,
  GET_ACTIVE_DELEGATION_SESSION_RESPONSE,
  GET_DELEGATION_SELLER,
  GET_DELEGATION_SELLER_RESPONSE,
  GET_POSSIBLE_BUYERS_TO_DELEGATE,
  GET_POSSIBLE_BUYERS_TO_DELEGATE_RESPONSE,
  GET_POSSIBLE_SELLERS_TO_DELEGATE,
  GET_POSSIBLE_SELLERS_TO_DELEGATE_RESPONSE,
  RESET_DELEGATION_SESSION,
} from './DelegationSession.types';

interface DelegationSessionRootState {
  delegationSession: DelegationSessionState;
}

export const initialState: DelegationSessionState = {
  activeSession: {
    searchStatus: null,
    data: null,
  },
  possibleSellers: {
    searchStatus: null,
    data: null,
  },
  possibleBuyers: new Map(),
  isConnecting: false,
  isDisconnecting: false,
  error: null,
  sseConnection: null,
  heartbeatActive: false,
  lastHeartbeatAt: null,
};

export const connectDelegationSessionRequest = createAction<ConnectDelegationRequest>(
  CONNECT_DELEGATION_SESSION_REQUEST,
);
export const disconnectDelegationSessionRequest = createAction(DISCONNECT_DELEGATION_SESSION_REQUEST);
export const extendDelegationSessionRequest = createAction<{ minutes?: number }>(EXTEND_DELEGATION_SESSION_REQUEST);
export const getActiveDelegationSessionRequest = createAction(GET_ACTIVE_DELEGATION_SESSION_REQUEST);
export const getPossibleSellersForDelegationSaga = createAction(GET_POSSIBLE_SELLERS_TO_DELEGATE);
export const getPossibleSellersForDelegationResponseSaga = createAction(GET_POSSIBLE_SELLERS_TO_DELEGATE_RESPONSE);
export const getDelegationSellerSaga = createAction<string>(GET_DELEGATION_SELLER);
export const cancelCurrentDelegationSellerSaga = createAction(CANCEL_CURRENT_DELEGATION_SELLER);
export const getDelegationSellerResponseSaga = createAction(GET_DELEGATION_SELLER_RESPONSE);
export const getPossibleBuyersForDelegationSaga = createAction<string>(GET_POSSIBLE_BUYERS_TO_DELEGATE);
export const resetDelegationSessionSaga = createAction(RESET_DELEGATION_SESSION);
export const getPossibleBuyersForDelegationResponseSaga = createAction(GET_POSSIBLE_BUYERS_TO_DELEGATE_RESPONSE);

export const connectDelegationSessionResponse = createAction(CONNECT_DELEGATION_SESSION_RESPONSE);
export const disconnectDelegationSessionResponse = createAction(DISCONNECT_DELEGATION_SESSION_RESPONSE);
export const extendDelegationSessionResponse = createAction(EXTEND_DELEGATION_SESSION_RESPONSE);
export const getActiveDelegationSessionResponse = createAction(GET_ACTIVE_DELEGATION_SESSION_RESPONSE);

export const delegationSessionUpdatedEvent = createAction(DELEGATION_SESSION_UPDATED_EVENT);
export const delegationSessionErrorAction = createAction(DELEGATION_SESSION_ERROR);

export const delegationContextSwitched = createAction('delegationSession/contextSwitched');
export const delegationContextReverted = createAction('delegationSession/contextReverted');
export const delegationContextRefreshRequest = createAction(
  'delegationSession/contextRefreshRequest',
  (payload?: { profileAlreadyPending?: boolean }) => ({ payload }),
);

export const startHeartbeat = createAction('delegationSession/startHeartbeat');
export const stopHeartbeat = createAction('delegationSession/stopHeartbeat');
export const heartbeatTick = createAction('delegationSession/heartbeatTick');

const slice = createSlice({
  name: DELEGATION_SESSION_NAMESPACE,
  reducers: {
    setInitialState: () => initialState,

    setConnecting: (state) => {
      state.isConnecting = true;
      state.isDisconnecting = false;
      state.error = null;
    },

    setConnected: (state, action: PayloadAction<DelegationSessionResponse>) => {
      state.activeSession = {
        searchStatus: FOUND,
        data: action.payload,
      };
      state.isConnecting = false;
      state.isDisconnecting = false;
      state.error = null;
    },

    setDisconnecting: (state) => {
      state.isDisconnecting = true;
      state.error = null;
    },

    setDisconnected: (state) => {
      state.activeSession = {
        searchStatus: NOT_FOUND,
        data: null,
      };
      state.isConnecting = false;
      state.isDisconnecting = false;
      state.error = null;
    },

    setSessionNotFound: (state) => {
      state.activeSession = {
        searchStatus: NOT_FOUND,
        data: null,
      };
      state.isConnecting = false;
      state.isDisconnecting = false;
    },

    setLoading: (state) => {
      state.activeSession = {
        searchStatus: LOADING,
        data: state.activeSession.data,
      };
    },

    updateSession: (state, action: PayloadAction<DelegationSessionResponse>) => {
      state.activeSession = {
        searchStatus: FOUND,
        data: action.payload,
      };
    },

    setPossibleBuyersForSellerToDelegate: (
      state,
      {
        payload,
      }: PayloadAction<{
        sellerId: string;
        buyers: DelegationBuyer[];
      }>,
    ) => {
      state.possibleBuyers.set(payload.sellerId, {
        searchStatus: FOUND,
        data: payload.buyers,
      });
    },

    setPossibleBuyersForSellerToDelegateNoData: (
      state,
      {
        payload,
      }: PayloadAction<{
        sellerId: string;
        status: SEARCH_STATUS | null;
      }>,
    ) => {
      state.possibleBuyers.set(payload.sellerId, {
        searchStatus: payload.status,
        data: null,
      });
    },

    setPossibleSellersToDelegate: (
      state,
      {
        payload,
      }: PayloadAction<{
        sellers: DelegationSeller[];
      }>,
    ) => {
      state.possibleSellers = {
        searchStatus: FOUND,
        data: payload.sellers,
      };
    },

    setPossibleSellersToDelegateNoData: (state, { payload }: PayloadAction<SEARCH_STATUS | null>) => {
      state.possibleSellers = {
        searchStatus: payload,
        data: null,
      };
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isConnecting = false;
      state.isDisconnecting = false;
    },

    clearError: (state) => {
      state.error = null;
    },

    setSseConnection: (state, action: PayloadAction<EventSource | null>) => {
      state.sseConnection = action.payload;
    },

    setHeartbeatActive: (state, action: PayloadAction<boolean>) => {
      state.heartbeatActive = action.payload;
      if (action.payload) {
        state.lastHeartbeatAt = Date.now();
      }
    },
  },
  initialState,
});

export const {
  setInitialState,
  setConnecting,
  setConnected,
  setDisconnecting,
  setDisconnected,
  setSessionNotFound,
  setLoading,
  updateSession,
  setError,
  clearError,
  setSseConnection,
  setHeartbeatActive,
  setPossibleBuyersForSellerToDelegate,
  setPossibleBuyersForSellerToDelegateNoData,
  setPossibleSellersToDelegate,
  setPossibleSellersToDelegateNoData,
} = slice.actions;

export default slice.reducer;

export const getActiveSession = (state: DelegationSessionRootState): DelegationSessionResponse | null =>
  state.delegationSession?.activeSession.data;
export const getActiveSessionStatus = (state: DelegationSessionRootState): SEARCH_STATUS | null =>
  state.delegationSession?.activeSession.searchStatus;
export const getIsConnecting = (state: DelegationSessionRootState): boolean =>
  state.delegationSession?.isConnecting ?? false;
export const getIsDisconnecting = (state: DelegationSessionRootState): boolean =>
  state.delegationSession?.isDisconnecting ?? false;
export const getError = (state: DelegationSessionRootState): string | null => state.delegationSession?.error;
export const getSseConnection = (state: DelegationSessionRootState): EventSource | null =>
  state.delegationSession?.sseConnection ?? null;

export const getPossibleDelegateSessionBuyers = createSelector(
  (state: DelegationSessionRootState) => state.delegationSession?.possibleBuyers,
  (_state: DelegationSessionRootState, sellerId: string | undefined) => sellerId,
  (buyerMap, sellerId) => (buyerMap && sellerId ? buyerMap.get(sellerId) : undefined),
);

export const getPossibleDelegateSessionSellersData = createSelector(
  (state: DelegationSessionRootState) => state.delegationSession?.possibleSellers,
  (sellersSearchData) => sellersSearchData,
);

export const getPossibleDelegateSessionSellers = createSelector(
  getPossibleDelegateSessionSellersData,
  (sellersSearchData) => sellersSearchData,
);

export const getIsSessionActive = createSelector([getActiveSession], (session) => session?.active ?? false);

export const getSessionExpiresAt = createSelector([getActiveSession], (session) => {
  if (!session?.expiresAt) {
    return null;
  }
  return new Date(session.expiresAt);
});

export const getSessionTimeRemaining = createSelector([getSessionExpiresAt], (expiresAt) => {
  if (!expiresAt) {
    return null;
  }
  const now = new Date();
  const remaining = expiresAt.getTime() - now.getTime();
  return Math.max(0, remaining);
});

export const getDelegatedBuyerInfo = createSelector([getActiveSession], (session) => {
  if (!session) {
    return null;
  }
  return {
    tresorCode: session.delegatedBuyerTresorCode,
    name: session.delegatedBuyerName,
  };
});

export const isDelegationSessionStatusLoading = createSelector(
  [getActiveSessionStatus],
  (status) => status === LOADING,
);

export const getDelegatedSellerInfo = createSelector([getActiveSession], (session) => {
  if (!session) {
    return null;
  }
  return {
    tresorCode: session.delegatedSellerTresorCode,
  };
});

export const getDelegatedBuyer = createSelector(
  [getActiveSession, (state: DelegationSessionRootState) => state.delegationSession?.possibleBuyers],
  (session, possibleBuyers) => {
    if (!session?.active || !session.delegatedSellerTresorCode || !session.delegatedBuyerTresorCode) {
      return null;
    }
    const buyersForSeller = possibleBuyers?.get(session.delegatedSellerTresorCode);
    if (!buyersForSeller?.data) {
      return null;
    }
    return buyersForSeller.data.find((b) => b.tresorCode === session.delegatedBuyerTresorCode) ?? null;
  },
);
